import { openDB, type DBSchema } from 'idb';
import type { Prayer } from '@/types/prayer';
import type { CompletePrayerDocument, ContentPartitionV1, LocalContentEntityV1 } from '@/types/content-lifecycle';
import { prayerFromCompleteDocument } from './content-lifecycle/contracts';

// Read-only view of the existing db.ts schema, not a new schema or upgrade path.
interface SavedPrayerDB extends DBSchema {
  'app-meta': { key: string; value: { key: string; value: unknown } };
  prayers: { key: string; value: Prayer };
  'content-entities': {
    key: [string, string, string]; value: LocalContentEntityV1;
    indexes: { 'by-owner': string };
  };
  'content-partitions': { key: string; value: ContentPartitionV1 };
}

export class OfflinePrayerReadError extends Error {
  readonly reason: 'unavailable' | 'owner-changed';
  constructor(reason: 'unavailable' | 'owner-changed') {
    super(`offline-prayers-${reason}`);
    this.reason = reason;
  }
}

export async function readOfflinePrayers(ownerId: string): Promise<Prayer[]> {
  let db;
  try {
    db = await openDB<SavedPrayerDB>('adia-db', undefined, {
      // Opening an evicted database must not create a fresh empty profile.
      upgrade(_database, _oldVersion, _newVersion, transaction) {
        // This deliberate refusal still rejects the open request. Observe its
        // separate transaction rejection before aborting; never create a DB.
        void transaction.done.catch(() => undefined);
        transaction.abort();
      },
    });
  } catch {
    throw new OfflinePrayerReadError('unavailable');
  }
  try {
    // Existing supported db.ts schema only; familiar stores in a newer DB
    // do not permit bypassing the app's client-update-required boundary.
    if (db.version !== 10) throw new OfflinePrayerReadError('unavailable');
    const names = ['app-meta', 'prayers', 'content-entities', 'content-partitions'] as const;
    if (names.some((name) => !db.objectStoreNames.contains(name))) {
      throw new OfflinePrayerReadError('unavailable');
    }
    const tx = db.transaction(names, 'readonly');
    const [binding, cleanup, partition, entities, legacy] = await Promise.all([
      tx.objectStore('app-meta').get('content-owner'),
      tx.objectStore('app-meta').get('owner-adoption-cleanup'),
      tx.objectStore('content-partitions').get(ownerId),
      tx.objectStore('content-entities').index('by-owner').getAll(ownerId),
      tx.objectStore('prayers').getAll(),
      // Observe completion alongside requests: a rejected request must not
      // skip observing the transaction's own rejection. Both still fail closed.
      tx.done,
    ]);
    if (binding?.value !== ownerId || cleanup !== undefined ||
        (partition && partition.ownerId !== ownerId) || entities.some((entity) => entity.ownerId !== ownerId)) {
      throw new OfflinePrayerReadError('owner-changed');
    }
    const prayerEntities = entities.filter((entity) => entity.entityType === 'prayer');
    const ids = new Set(prayerEntities.map((entity) => entity.entityId));
    const legacyById = new Map(legacy.map((prayer) => [prayer.id, prayer]));
    const initialized = partition?.legacyBootstrapComplete === true ||
      partition?.legacyBootstrapEntityTypes?.includes('prayer') === true;
    const prayers = !initialized && prayerEntities.length === 0 ? legacy : [
      ...prayerEntities.flatMap((entity) => {
        if (entity.visibleDocument === null) return [];
        if (entity.visibleDocument.id !== entity.entityId) throw new OfflinePrayerReadError('unavailable');
        return [prayerFromCompleteDocument(entity.visibleDocument as CompletePrayerDocument, legacyById.get(entity.entityId))];
      }),
      ...legacy.filter((prayer) => Boolean(prayer.pendingTranscriptionId) && !ids.has(prayer.id)),
    ];
    // Corrupt rows do not become a partial, misleading success or raw JSX values.
    for (const prayer of prayers) {
      if (typeof prayer.id !== 'string' || typeof prayer.rawText !== 'string' ||
          typeof prayer.createdAt !== 'string' || typeof prayer.updatedAt !== 'string' ||
          !['Open', 'In Progress', 'Moving', 'Answered'].includes(prayer.status) ||
          !Array.isArray(prayer.progressHistory) ||
          prayer.progressHistory.some((entry) => !entry || typeof entry.note !== 'string' || typeof entry.date !== 'string') ||
          (prayer.testimony != null && typeof prayer.testimony !== 'string') ||
          (prayer.parsed != null && typeof prayer.parsed.who !== 'string')) {
        throw new OfflinePrayerReadError('unavailable');
      }
    }
    return prayers.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
  } catch (error) {
    if (error instanceof OfflinePrayerReadError) throw error;
    throw new OfflinePrayerReadError('unavailable');
  } finally {
    db.close();
  }
}
